import * as XLSX from 'xlsx';

/**
 * Shared Excel export — used by every page instead of page-local XLSX code.
 *
 * Uses the SAME column config as the Shared PDF Engine:
 *   { key, label, excelValue?: (row) => primitive }
 *
 * Always adds a '#' first column with continuous numbering, preserves the
 * column order given, and supports Arabic/English content transparently
 * (xlsx writes UTF-8; RTL is a viewer setting).
 *
 * @param {{
 *   columns: Array<{key?: string, label: string, excelValue?: Function}>,
 *   rows: Array<object>,
 *   fileName: string,          // without extension
 *   sheetName?: string,
 *   startIndex?: number,       // continuous numbering offset
 *   rtl?: boolean,
 * }} params
 */
export const exportRowsToExcel = ({
  columns,
  rows,
  fileName,
  sheetName = 'Sheet1',
  startIndex = 0,
  rtl = false,
}) => {
  const header = ['#', ...columns.map(col => col.label)];

  const dataRows = (rows || []).map((row, idx) => [
    startIndex + idx + 1,
    ...columns.map(col => {
      if (col.excelValue) return col.excelValue(row);
      const value = row?.[col.key];
      return value === null || value === undefined ? '' : value;
    }),
  ]);

  const ws = XLSX.utils.aoa_to_sheet([header, ...dataRows]);

  // Reasonable default column widths based on content length.
  ws['!cols'] = header.map((label, colIdx) => {
    const maxLen = Math.max(
      String(label).length,
      ...dataRows.map(r => String(r[colIdx] ?? '').length),
    );
    return { wch: Math.min(Math.max(maxLen + 2, 6), 60) };
  });

  if (rtl) {
    ws['!views'] = [{ rightToLeft: true }];
  }

  const wb = XLSX.utils.book_new();
  // Excel sheet names are limited to 31 chars and a few forbidden characters.
  const safeSheetName = String(sheetName)
    .replace(/[\\/?*[\]:]/g, ' ')
    .slice(0, 31)
    .trim() || 'Sheet1';
  XLSX.utils.book_append_sheet(wb, ws, safeSheetName);

  const datePart = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `${fileName}_${datePart}.xlsx`);
};

export default exportRowsToExcel;
